import useAxios from '@hooks/useFetch';
import { components, paths } from '@api/api';
import { useCallback, useMemo } from 'react';
import { Sort } from "@models";

const TOP_TRENDS_MENU_EXAMPLES_ENDPOINT: keyof paths = '/TopTrendMenuExamples';

export type TopTrendsMenuExamplesRequest = components['schemas']['TopTrendMenuExamplesFilterInput'];
export type TopTrendsMenuExamplesRequestParameters = components['schemas']['TopTrendMenuExamplesSortParameters'];
export type TopTrendsMenuExamplesResponse = components['schemas']['MenuExamplesOutputModel'];
export type MenuItemExample = components["schemas"]["MenuItemExample"]
export type TopTrendsMenuExamplesExportRequest = components['schemas']['TopTrendMenuExampleExportFilter'];
export type FilterWord = components['schemas']['FilterWord'];
export type MainFilter = components['schemas']['BaseTopTrendMainFilterInput'];
export type Filter = components['schemas']['MainInputFilter'];

type FetchOptions = {
    sortColumn: TopTrendsMenuExamplesRequestParameters['sortColumn'];
    search: string;
    take: number;
    skip: number;
    sortDir: string;
};

type FetchResponse = {
    data: MenuItemExample[];
    totalRecords: number;
};

export interface UseTopTrendsMenuExamplesParams {
    word: FilterWord | null;
    mainFilter: MainFilter;
    fullFilter?: TopTrendsMenuExamplesExportRequest['fullFilter'];
}

export default function useTopTrendsMenuExamples({
    word,
    mainFilter,
    fullFilter,
}: UseTopTrendsMenuExamplesParams) {
    const {
        doFetch,
        cancel: cancelFetch,
    } = useAxios<TopTrendsMenuExamplesResponse, TopTrendsMenuExamplesRequest>({
        method: 'post',
        url: TOP_TRENDS_MENU_EXAMPLES_ENDPOINT,
        lazy: true,
    });

    const fetch = useCallback(
        async (options: FetchOptions): Promise<FetchResponse> => {
            if (!word) {
                return { data: [], totalRecords: 0 };
            }

            const requestData: TopTrendsMenuExamplesRequest = {
                mainFilter: {
                    ...mainFilter,
                    word,
                },
                tableParameters: {
                    sortColumn: options.sortColumn,
                    search: options.search,
                    take: options.take,
                    skip: options.skip,
                    sortDir: options.sortDir === Sort.Asc ? 0 : 1,
                },
            };

            const response = await doFetch({ data: requestData });

            return {
                data: response?.data?.data ?? [],
                totalRecords: response?.data?.totalRecords ?? 0,
            };
        },
        [word, mainFilter, doFetch]
    );

    const getExportData = useCallback((options: FetchOptions) => {
        if (!word) return;

        return {
            fullFilter,
            customerFilter: {
                mainFilter: {
                    ...mainFilter,
                    word,
                },
                tableParameters: {
                    sortColumn: options.sortColumn,
                    search: options.search,
                    take: options.take,
                    skip: options.skip,
                    sortDir: options.sortDir === Sort.Asc ? 0 : 1,
                },
            },
        };
    }, [word, mainFilter, fullFilter]);

    const cancel = useCallback(() => {
        cancelFetch();
    }, [cancelFetch]);

    const isReady = useMemo(() => !!word, [word]);

    return {
        fetch,
        getExportData,
        cancel,
        isReady,
    };
}
